/**
 * One shared 3D belly for the whole app. Three.js is loaded on demand (its own chunk),
 * so the first screens open fast. Falls back to a big tap button without WebGL.
 */
import type { BellyHit, Belly } from '../three/belly';

let belly: Belly | null = null;
let loading: Promise<Belly | null> | null = null;

export type TapHandler = (hit: BellyHit | null, screen: { x: number; y: number }) => void;

/** Load the 3D chunk; retry once (a deploy or a flaky network can drop it), then give up gracefully. */
async function importBelly(): Promise<typeof import('../three/belly') | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try { return await import('../three/belly'); }
    catch (e) {
      console.warn('[3d] could not load the 3D belly', e);
      if (attempt === 0) await new Promise(r => setTimeout(r, 800));
    }
  }
  // Never reload here: it would throw away what she typed during onboarding. The big tap button still works.
  return null;
}

async function load(): Promise<Belly | null> {
  const mod = await importBelly();
  if (!mod) return null;  if (!mod.webglAvailable()) return null;
  const canvas = document.createElement('canvas');
  canvas.id = 'belly-canvas';
  canvas.setAttribute('aria-label', 'belly');
  const holder = document.createElement('div');
  try {
    return new mod.Belly(canvas, holder, { onTap: () => {} });
  } catch (e) {
    console.warn('[3d] WebGL failed, using fallback', e);
    return null;
  }
}

function plusOne(stage: HTMLElement, x: number, y: number) {
  const el = document.createElement('div');
  el.className = 'plus-one';
  el.textContent = '+1';
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  stage.appendChild(el);
  setTimeout(() => el.remove(), 800);
}

/** Put the belly into `stage`. Returns false if it fell back to the simple button. */
export async function mountBelly(stage: HTMLElement, onTap: TapHandler, opts: { spatial: boolean; markers?: { x: number; y: number; z: number }[] }): Promise<boolean> {
  loading ??= load();
  belly ??= await loading;
  if (!stage.isConnected) return !!belly;
  if (!belly) {
    stage.insertAdjacentHTML('beforeend', '<div class="stage-fallback"><button class="tap-big" type="button">👣</button></div>');
    stage.querySelector('.tap-big')!.addEventListener('click', ev => {
      const r = stage.getBoundingClientRect();
      const me = ev as MouseEvent;
      plusOne(stage, me.clientX - r.left, me.clientY - r.top);
      onTap(null, { x: me.clientX - r.left, y: me.clientY - r.top });
    });
    return false;
  }
  const canvas = document.getElementById('belly-canvas') as HTMLCanvasElement | null;
  if (canvas) canvas.hidden = false;
  belly.attach(stage, (hit, pt) => { plusOne(stage, pt.x, pt.y); onTap(hit, pt); });
  belly.setSpatial(opts.spatial);
  if (opts.markers) belly.showMarkers(opts.markers); else belly.clearMarkers();
  belly.start();
  return true;
}

export function unmountBelly(): void { belly?.stop(); }
export function bellyRemoveLast(): void { belly?.removeLastMarker(); }
export function bellySetSpatial(on: boolean): void { belly?.setSpatial(on); }
/** For automated tests: screen coordinates of a point on the bump. */
export function bellyFrontPoint(dx = 0, dy = 0): { x: number; y: number } | null { return belly ? belly.frontPoint(dx, dy) : null; }
